import { useState, useEffect } from 'react'
import { useParams, useSearchParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import SimpleAuthCard from './SimpleAuthCard.jsx'
import Button from '../../components/ui/Button.jsx'
import { Field, Input } from '../../components/ui/Field.jsx'
import Icon from '../../components/ui/Icon.jsx'
import api from '../../lib/api.js'
import { passwordChecks, isValidPassword, passwordScore } from '../../lib/password.js'

/* Set a new password from an emailed token — inventory §3. */
export default function ResetPasswordPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { token: tokenParam } = useParams()
  const [params] = useSearchParams()
  const token = tokenParam || params.get('token') || ''

  const [pw, setPw] = useState('')
  const [confirm, setConfirm] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [done, setDone] = useState(false)

  useEffect(() => { if (!token) setError(t('auth.reset.missingToken')) }, [token, t])
  useEffect(() => {
    if (!done) return undefined
    const id = setTimeout(() => navigate('/login'), 3000)
    return () => clearTimeout(id)
  }, [done, navigate])

  const checks = passwordChecks(pw)
  const score = passwordScore(pw)
  const mismatch = confirm && pw !== confirm

  const submit = async (e) => {
    e.preventDefault()
    setError('')
    if (pw !== confirm) { setError(t('auth.reset.mismatch')); return }
    if (!isValidPassword(pw)) { setError(t('auth.reset.weak')); return }
    setLoading(true)
    try { await api.post('/auth/reset-password', { token, new_password: pw, confirm_password: confirm }); setDone(true) }
    catch (e2) { setError(e2?.response?.data?.error || t('auth.reset.error')) }
    finally { setLoading(false) }
  }

  if (done) {
    return (
      <SimpleAuthCard title={t('auth.reset.successTitle')}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: 52, height: 52, borderRadius: '50%', background: 'var(--color-success-bg)', color: 'var(--color-success)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}><Icon name="check" size={26} /></div>
          <p style={{ color: 'var(--color-text)', fontSize: 14, marginBottom: 16 }}>{t('auth.reset.successMessage')}</p>
          <Button variant="primary" icon="logout" onClick={() => navigate('/login')}>{t('auth.reset.loginNow')}</Button>
        </div>
      </SimpleAuthCard>
    )
  }

  const barColor = ['#e6e1f2', 'var(--color-danger)', 'var(--color-accent)', 'var(--color-success)'][score]
  return (
    <SimpleAuthCard title={t('auth.reset.title')} subtitle={t('auth.reset.subtitle')}>
      {error && <div role="alert" style={{ background: 'var(--color-danger-bg)', color: 'var(--color-danger)', border: '1px solid #f3c9c9', borderRadius: 10, padding: '10px 13px', fontSize: 13, marginBottom: 16 }}>{error}</div>}
      <form onSubmit={submit}>
        <Field label={t('auth.reset.newPassword')}><Input type="password" autoComplete="new-password" required value={pw} onChange={(e) => setPw(e.target.value)} /></Field>
        {pw && (
          <div style={{ marginTop: -8, marginBottom: 14 }}>
            <div style={{ height: 5, borderRadius: 3, background: '#eee', overflow: 'hidden', marginBottom: 8 }}><div style={{ height: '100%', width: `${(score / 3) * 100}%`, background: barColor }} /></div>
            <div style={{ display: 'flex', gap: 12, fontSize: 12 }}>
              {[['length', t('settings.password.ruleLength')], ['digit', t('settings.password.ruleDigit')], ['special', t('settings.password.ruleSpecial')]].map(([k, label]) => (
                <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: checks[k] ? 'var(--color-success)' : 'var(--color-text-muted)' }}><Icon name={checks[k] ? 'check' : 'x'} size={13} /> {label}</span>
              ))}
            </div>
          </div>
        )}
        <Field label={t('auth.reset.confirm')} error={mismatch ? t('auth.reset.mismatch') : undefined}>
          <Input type="password" autoComplete="new-password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} style={mismatch ? { borderColor: 'var(--color-danger)' } : undefined} />
        </Field>
        <Button type="submit" variant="primary" icon="check" fullWidth loading={loading} disabled={!token}>{t('auth.reset.submit')}</Button>
      </form>
    </SimpleAuthCard>
  )
}
